import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Save, X, Edit3, Eye } from 'lucide-react';

const NewsEditModal = ({ news, isOpen, onClose, onSave }) => {
  const [editedNews, setEditedNews] = useState({
    title: '',
    category: '',
    lead: '',
    description: '',
  });
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const categories = ['Política', 'Deportes', 'Tecnología', 'Ciencias e investigación', 'Medioambiente', 'Industria y negocios', 'Educación', 'Economía', 'Cultura y entretenimiento'];


  // Configuración del editor ReactQuill
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image'],
      [{ 'align': [] }],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image', 'align', 'color', 'background'
  ];

  // Inicializar el estado cuando se abra el modal
  useEffect(() => {
    if (isOpen && news) {
      setEditedNews({
        title: news.title || '',
        category: news.category || '',
        lead: news.lead || '',
        description: news.description || '',
      });
      setIsPreviewMode(false);
    }
  }, [isOpen, news]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(news.id, editedNews);
      onClose();
    } catch (error) {
      console.error('Error saving news:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedNews(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const parseDateToLocal = (dateString) => {
    if (!dateString) return null;
    const cleanDateString = dateString.split('T')[0];
    const parts = cleanDateString.split('-');
    if (parts.length !== 3) return null;
    const [year, month, day] = parts.map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    return new Date(year, month - 1, day);
  };

  const leadModules = {
        toolbar: [
            ['bold'],
        ],
    };


  if (!isOpen || !news) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div className="flex items-center gap-3">
            <Edit3 size={24} className="text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">
              {isPreviewMode ? 'Vista Previa' : 'Editar Noticia'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                isPreviewMode 
                  ? 'bg-blue-500 text-white hover:bg-blue-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {isPreviewMode ? <Edit3 size={16} /> : <Eye size={16} />}
              {isPreviewMode ? 'Editar' : 'Vista Previa'}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Información básica */}
          <div className="mb-6 bg-gray-50 p-4 rounded-lg">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
              <span>Categoría: {news.category}</span>
              <span>
                Fecha: {news.creationDate && parseDateToLocal(news.creationDate).toLocaleDateString()}
              </span>
              <span>
                Autor: {news.company?.trade_name || news.companyName || 'Desconocido'}
              </span>
            </div>
          </div>

          {isPreviewMode ? (
            /* Modo Vista Previa */
            <div className="space-y-6">
              {/* Título en preview */}
              <div>
                <h3 className="text-3xl font-bold text-gray-800 mb-4">
                  {editedNews.title}
                </h3>
              </div>

              {/* Imagen en preview */}
              {editedNews.image && (
                <div className="flex justify-center">
                  <img 
                    src={editedNews.image} 
                    alt={editedNews.title}
                    className="max-w-full h-auto rounded-lg shadow-md"
                    loading="lazy"
                  />
                </div>
              )}

              {/* Lead en preview */}
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <div 
                  className="text-gray-700 text-lg italic"
                  dangerouslySetInnerHTML={{ __html: editedNews.lead }}
                />
              </div>

              {/* Descripción en preview */}
              <div className="prose max-w-none">
                <div 
                  className="text-gray-700 text-justify"
                  dangerouslySetInnerHTML={{ __html: editedNews.description }}
                />
              </div>
            </div>
          ) : (
            /* Modo Edición */
            <div className="space-y-6">
              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título
                </label>
                <input
                  type="text"
                  value={editedNews.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Título de la noticia"
                />
              </div>

              {/* Categoría */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoría
                </label>
                <select
                  name='category'
                  value={editedNews.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Categoría"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              {/* Lead/Bajada */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bajada (Lead)
                </label>
                <ReactQuill
                  theme='snow'
                  value={editedNews.lead}
                  onChange={(value) => handleInputChange('lead', value)}
                  modules={leadModules}
                  style={{ height: '150px', marginBottom: '50px' }}
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción completa
                </label>
                <ReactQuill
                  value={editedNews.description}
                  onChange={(value) => handleInputChange('description', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: '300px', marginBottom: '50px' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
          >
            Cancelar
          </button>
          
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={16} />
            {isSaving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsEditModal;